// Catálogo local de medicamentos — Fase 5, sección 8.2. Import real y ÚNICO
// de ARCSA (ver scripts/importar-catalogo-arcsa.mjs), servido como asset
// estático (public/catalogo-medicamentos.json, publicDir de esta app) y
// jamás bundleado en el JS — se trae con `fetch` la primera vez que se abre
// el wizard de receta y se cachea en memoria del proceso (módulo singleton,
// no localStorage: 4+ MB por pestaña alcanza, no hace falta persistirlo).
import { useEffect, useState } from 'react';

export interface MedicamentoCatalogo {
  nombre: string;
  marca: string;
  forma: string;
  via: string;
  concentracion: string;
  dci: string;
  cum: string;
  /**
   * "Bajo receta controlada" | "Bajo receta médica" | "Libre" — cuando dice
   * "controlada", FIJA la bandera de sustancia controlada de forma
   * obligatoria (candado duro, ver `datosDeCatalogo` en RecetaWizard.tsx):
   * el catálogo oficial de ARCSA manda, el médico ya no puede desmarcarla.
   * Para cualquier otro valor (o entrada manual sin catálogo) la bandera
   * sigue siendo enteramente editable por el médico.
   */
  formaVenta: string;
}

let cache: Promise<MedicamentoCatalogo[]> | undefined;

function cargarCatalogo(): Promise<MedicamentoCatalogo[]> {
  if (!cache) {
    cache = fetch('/catalogo-medicamentos.json')
      .then((res) => {
        if (!res.ok) throw new Error(`No se pudo cargar el catálogo de medicamentos (HTTP ${res.status}).`);
        return res.json() as Promise<MedicamentoCatalogo[]>;
      })
      .catch((err: unknown) => {
        cache = undefined; // permite reintentar en el próximo render, no se queda pegado en el error.
        throw err;
      });
  }
  return cache;
}

export type EstadoCatalogo =
  | { cargando: true; error: null }
  | { cargando: false; error: null; medicamentos: MedicamentoCatalogo[] }
  | { cargando: false; error: string };

export function useCatalogoMedicamentos(): EstadoCatalogo {
  const [estado, setEstado] = useState<EstadoCatalogo>({ cargando: true, error: null });

  useEffect(() => {
    let cancelado = false;
    cargarCatalogo()
      .then((medicamentos) => {
        if (!cancelado) setEstado({ cargando: false, error: null, medicamentos });
      })
      .catch((err: unknown) => {
        if (!cancelado) setEstado({ cargando: false, error: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelado = true;
    };
  }, []);

  return estado;
}

const MAX_RESULTADOS = 25;

/**
 * Filtro simple por substring (sin dependencia nueva) sobre nombre/marca/DCI
 * — alcanza para 15 mil registros con la lista acotada a MAX_RESULTADOS.
 * Dedupea por `dci+nombre` (la misma etiqueta que arma RecetaWizard para el
 * combobox): el catálogo real trae presentaciones distintas (registro
 * sanitario/fabricante) con exactamente el mismo texto visible, mostrar el
 * duplicado no aporta nada al médico, que igual completa forma/concentración
 * a mano después.
 */
export function buscarEnCatalogo(medicamentos: MedicamentoCatalogo[], consulta: string): MedicamentoCatalogo[] {
  const q = consulta.trim().toLowerCase();
  if (q.length < 2) return [];
  const vistos = new Set<string>();
  const resultados: MedicamentoCatalogo[] = [];
  for (const m of medicamentos) {
    if (m.nombre.toLowerCase().includes(q) || m.dci.toLowerCase().includes(q) || m.marca.toLowerCase().includes(q)) {
      const etiqueta = `${m.dci} — ${m.nombre}`;
      if (vistos.has(etiqueta)) continue;
      vistos.add(etiqueta);
      resultados.push(m);
      if (resultados.length >= MAX_RESULTADOS) break;
    }
  }
  return resultados;
}

/**
 * ARCSA marca la marca ausente con MUCHAS variantes placeholder distintas
 * ('N/A', 'n/a', 'NA', '-', 'N.A.', 'NO APLICA', 'No aplica', '.', '.-.',
 * 'S/N'... — 5 mil+ registros en total, ver el import real) — ninguna es un
 * nombre comercial real. Se normaliza sacando todo lo que no sea letra/
 * número antes de comparar, así "N.A." y "NA" caen en el mismo bucket sin
 * mantener una lista interminable de variantes de puntuación. Compartido
 * con `datosDeCatalogo` (RecetaWizard.tsx) para no divergir el criterio.
 */
const MARCAS_PLACEHOLDER = new Set(['na', 'noaplica', 'sn']);

export function marcaValida(marca: string): boolean {
  const compacto = marca.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  return compacto.length > 0 && !MARCAS_PLACEHOLDER.has(compacto);
}

/**
 * Nombres comerciales sugeridos para un DCI ya elegido/tipeado — pedido
 * explícito del usuario 2026-09-02: una vez tipeado el DCI, "Nombre
 * comercial" también autocompleta contra el mismo catálogo ARCSA. El campo
 * `dci` de este catálogo no es un DCI farmacológico limpio en todos los
 * registros (a veces ya trae dosis/forma pegada, ej. "IBUPROFENO 400 MG
 * TABLETAS" — gotcha real del import, ver importar-catalogo-arcsa.mjs) —
 * por eso el match es por SUBSTRING (mismo criterio que `buscarEnCatalogo`),
 * no exacto: alcanza con que el DCI tipeado esté contenido en el del
 * registro. `filtroMarca` acota más si el médico ya empezó a tipear el
 * nombre comercial.
 */
export function marcasSugeridas(medicamentos: MedicamentoCatalogo[], dci: string, filtroMarca = ''): string[] {
  const dciQ = dci.trim().toLowerCase();
  if (dciQ.length < 2) return [];
  const marcaQ = filtroMarca.trim().toLowerCase();
  const marcas = new Set<string>();
  for (const m of medicamentos) {
    if (!m.dci.toLowerCase().includes(dciQ)) continue;
    if (!marcaValida(m.marca)) continue;
    if (marcaQ && !m.marca.toLowerCase().includes(marcaQ)) continue;
    marcas.add(m.marca.trim());
    if (marcas.size >= MAX_RESULTADOS) break;
  }
  return [...marcas].sort((a, b) => a.localeCompare(b));
}
